import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, FormGroup, ControlLabel, FormControl } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faCircle, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Modal } from '@vibrent/electryon';
import styles from './styles.scss';

const { InformationModal } = Modal;

const PASSWORD_MATCH_ERROR_MESSAGE = 'The new password cannot be the same as the old password.';

const REQUIREMENTS_TEXT = 'Meet the requirements below for a sufficiently strong password.';

const PASSWORD_REQUIREMENTS = [
  { label: 'Minimum 8 characters', validate: p => p.length >= 8 },
  { label: 'Contains at least one uppercase letter', validate: p => /[A-Z]/.test(p) },
  { label: 'Contains at least one lowercase letter', validate: p => /[a-z]/.test(p) },
  { label: 'Contains at least one number', validate: p => /\d/.test(p) },
  { label: 'Contains at least one special character', validate: p => /[^A-Za-z0-9]/.test(p) },
];

const ChangePasswordModal = (props) => {
  const {
    show,
    setIsShowConfirmModal,
    key,
    handleSubmit,
    userName,
  } = props;

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordMatchError, setPasswordMatchError] = useState(false);
  const [failureError, setFailureError] = useState(null);

  const requirementStatus = useMemo(
    () => PASSWORD_REQUIREMENTS.map(({ validate }) => validate(newPassword)),
    [newPassword],
  );

  const isNewPasswordValid = useMemo(
    () => requirementStatus.every(status => status),
    [requirementStatus],
  );

  const updateOldPassword = (value) => {
    if (passwordMatchError) {
      setPasswordMatchError(false);
    }
    if (failureError) {
      setFailureError(null);
    }

    setOldPassword(value);
  };

  const updateNewPassword = (value) => {
    if (passwordMatchError) {
      setPasswordMatchError(false);
    }
    if (failureError) {
      setFailureError(null);
    }

    setNewPassword(value);
  };

  useEffect(() => {
    if (
      oldPassword.length > 0 &&
      newPassword.length > 0 &&
      oldPassword === newPassword
    ) {
      setPasswordMatchError(true);
    }
  }, [oldPassword, newPassword]);

  const closeAndReset = () => {
    setOldPassword('');
    setNewPassword('');
    setShowOldPassword(false);
    setShowNewPassword(false);
    setIsShowConfirmModal(false);
    setPasswordMatchError(false);
    setFailureError(null);
  };

  const onClose = () => {
    closeAndReset();
  };

  const onSubmit = () => {
    const onSuccess = () => {
      closeAndReset();
    };

    const displayErrorMessage = (error) => {
      setFailureError(error);
    };

    handleSubmit(oldPassword, newPassword, onSuccess, displayErrorMessage);
  };

  const titleModal = 'Change Password';

  const isPasswordMatchError = useMemo(
    () => oldPassword.length > 0 && newPassword.length > 0 && oldPassword === newPassword,
    [oldPassword, newPassword],
  );

  const confirmModal = {
    title: titleModal,
    body: (
      <div className="body-content">
        <FormGroup controlId="change-password-username">
          <ControlLabel>Username</ControlLabel>
          <br />
          <span className={styles['username-display']}>{userName}</span>
        </FormGroup>
        <FormGroup controlId="change-password-old">
          <ControlLabel>Verify Current Password</ControlLabel>
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowOldPassword(prev => !prev)}
            aria-label={showOldPassword ? 'Hide password' : 'Show password'}
            tabIndex={-1}
          >
            <FontAwesomeIcon icon={showOldPassword ? faEyeSlash : faEye} />
          </button>
          <div className={styles['password-input-wrapper']}>
            <FormControl
              type={showOldPassword ? 'text' : 'password'}
              value={oldPassword}
              onChange={e => updateOldPassword(e.target.value)}
              placeholder="Enter current password"
              autoComplete="current-password"
              className={styles['password-input']}
            />
          </div>
        </FormGroup>
        <FormGroup controlId="change-password-new">
          <ControlLabel>New Password</ControlLabel>
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowNewPassword(prev => !prev)}
            aria-label={showNewPassword ? 'Hide password' : 'Show password'}
            tabIndex={-1}
          >
            <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
          </button>
          <div className={styles['password-input-wrapper']}>
            <FormControl
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={e => updateNewPassword(e.target.value)}
              placeholder="Enter new password"
              autoComplete="new-password"
              className={styles['password-input']}
            />
          </div>
          {failureError ? (
            <span className="pwd-error-text">
              {failureError}
            </span>
          ) : (
            <span
              className={passwordMatchError ? 'pwd-error-text' : 'requirements-text'}
            >
              {passwordMatchError ? PASSWORD_MATCH_ERROR_MESSAGE : REQUIREMENTS_TEXT}
            </span>
          )}
        </FormGroup>
        <div className={styles['password-requirements']}>
          <div className={styles['password-requirements-title']}>Password requirements:</div>
          <ul className={styles['password-requirements-list']}>
            {PASSWORD_REQUIREMENTS.map((req, index) => (
              <li key={req.label} className={styles['password-requirement-item']}>
                <span className={styles['password-requirement-icon']} aria-hidden>
                  <FontAwesomeIcon
                    icon={requirementStatus[index] ? faCheck : faCircle}
                    className={requirementStatus[index] ? styles['requirement-met'] : styles['requirement-unmet']}
                  />
                </span>
                <span>
                  {req.label}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    ),
    btnSubmit: {
      text: 'Submit',
      bsStyle: 'primary',
      className: 'btn-continue',
      onClick: onSubmit,
    },
  };

  const renderFooter = () => (
    <div className="submit-password-button">
      <Button
        aria-label={confirmModal.btnSubmit.text}
        className={`btn-md ${confirmModal.btnSubmit.className
          ? confirmModal.btnSubmit.className : ''}`.trim()}
        bsStyle={confirmModal.btnSubmit.bsStyle}
        onClick={confirmModal.btnSubmit.onClick}
        disabled={!isNewPasswordValid || isPasswordMatchError}
      >
        {confirmModal.btnSubmit.text}
      </Button>
    </div>
  );

  return (show && <InformationModal
    id={key}
    className={styles['info-modal-template']}
    aria-modal="true"
    backdrop="static"
    show={show}
    title={confirmModal.title}
    body={confirmModal.body}
    footer={renderFooter()}
    onHide={onClose}
  />);
};

ChangePasswordModal.propTypes = {
  show: PropTypes.bool,
  key: PropTypes.string,
  setIsShowConfirmModal: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  userName: PropTypes.string.isRequired,
};

ChangePasswordModal.defaultProps = {
  show: false,
  key: 'confirm-modal',
  userName: '',
};

export default ChangePasswordModal;
